import { pacificToday } from '../../../shared/dates';
import type { TouchChannel } from '../../../shared/enums';
import { IN_PERSON_CHANNELS } from '../../../shared/enums';
import type { ExportField, ExportProfile } from '../../../shared/export';
import type { IsoDate, Lead, Visit } from '../../../shared/types';
import { buildCsv } from './csv';
import { formatTime } from './dates';
import {
  CHANNEL_LABELS,
  CHANNEL_PLURALS,
  OUTCOME_LABELS,
  PERSONA_LABELS,
  PLAYBOOK_VARIANT_LABELS,
  SPOKE_WITH_LABELS,
} from '../ui/labels';

/**
 * The activity log (D90) and its Salesforce transfer (D91). Pure, on the device, offline: every
 * touch is a `visits` row, and the export is a mapping the rep owns — the app never talks to
 * Salesforce and never sends anything anywhere.
 */

export interface Touch {
  visit: Visit;
  lead: Lead | null;
}

/** The touches that are visits (D90): standing in front of them. Everything that counts "visits" —
 *  the Home's Performance, Visits by day, the Reports history, the Overview tiles — reads this, so
 *  a word that says visit still means one now that a call and an email live in the same table.
 *  Rows written before D90 have no channel and were all walk-ins. */
export const inPersonOnly = (visits: readonly Visit[]): Visit[] =>
  visits.filter((v) => IN_PERSON_CHANNELS.includes(v.channel ?? 'walk_in'));

/** Touches newest first, with their lead; a touch whose lead was deleted keeps its row and says so. */
export const touchesOf = (visits: readonly Visit[], leads: readonly Lead[]): Touch[] => {
  const byId = new Map(leads.map((l) => [l.id, l]));
  return [...visits]
    .sort((a, b) => (a.visited_at < b.visited_at ? 1 : a.visited_at > b.visited_at ? -1 : 0))
    .map((visit) => ({ visit, lead: byId.get(visit.lead_id) ?? null }));
};

export interface TouchFilter {
  /** Local calendar days, inclusive; both ends optional. */
  from?: IsoDate;
  to?: IsoDate;
  channel?: TouchChannel | 'all';
  leadId?: string;
}

const dayOf = (iso: string): IsoDate => pacificToday(new Date(iso));

export const filterTouches = (touches: readonly Touch[], filter: TouchFilter): Touch[] =>
  touches.filter(({ visit }) => {
    const day = dayOf(visit.visited_at);
    if (filter.from && day < filter.from) return false;
    if (filter.to && day > filter.to) return false;
    if (filter.channel && filter.channel !== 'all' && visit.channel !== filter.channel) return false;
    if (filter.leadId && visit.lead_id !== filter.leadId) return false;
    return true;
  });

// ---------------------------------------------------------------- the Salesforce mapping

/** Our value under the rep's mapping, falling back to our own label when he has not mapped it. */
const mapped = <K extends string>(
  maps: Partial<Record<K, string>> | undefined,
  key: K,
  fallback: string,
): string => maps?.[key] ?? fallback;

/** One touch as the export fields see it. Everything is a string: a CSV cell and a pasted line. */
export const exportRow = (touch: Touch, profile: ExportProfile): Record<ExportField, string> => {
  const { visit, lead } = touch;
  const channel = visit.channel ?? 'walk_in';
  const type = mapped(profile.values.channel, channel, CHANNEL_LABELS[channel]);
  const name = lead?.business_name ?? '(deleted lead)';
  const at = new Date(visit.visited_at);
  return {
    subject: `${CHANNEL_LABELS[channel]} — ${name}`,
    company: name,
    type,
    status: 'Completed',
    priority: 'Normal',
    activity_date: dayOf(visit.visited_at),
    activity_time: formatTime(
      `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`,
    ),
    call_result: mapped(profile.values.outcome, visit.outcome, OUTCOME_LABELS[visit.outcome]),
    spoke_with: mapped(profile.values.spoke_with, visit.spoke_with, SPOKE_WITH_LABELS[visit.spoke_with]),
    persona: visit.persona ? PERSONA_LABELS[visit.persona] : '',
    message_variant: visit.variant ? PLAYBOOK_VARIANT_LABELS[visit.variant] : '',
    description: visit.notes ?? '',
    next_step: visit.next_action ?? '',
    next_step_date: visit.next_action_date ?? '',
    contact_name: lead?.owner_name ?? '',
    phone: lead?.phone ?? '',
    email: lead?.email ?? '',
    street: lead?.address ?? '',
    city: lead?.address_city ?? lead?.neighborhood ?? '',
    postal_code: lead?.address_postal_code ?? '',
    place_id: lead?.place_id ?? '',
    lead_stage: lead?.stage ?? '',
    lead_source: profile.lead_source,
    crm_lead_id: visit.lead_id,
    crm_touch_id: visit.id,
  };
};

/** Excel decodes a CSV as the locale's legacy code page unless it starts with a byte-order mark. */
const BOM = '﻿';

export const touchesCsv = (touches: readonly Touch[], profile: ExportProfile): string => {
  const columns = profile.columns.filter((c) => c.on);
  const rows = touches.map((t) => {
    const row = exportRow(t, profile);
    return columns.map((c) => row[c.field]);
  });
  return BOM + buildCsv(columns.map((c) => c.header), rows);
};

const TOKEN = /\{([a-z_]+)\}/g;

/** One line of the block. A line whose every field came out empty is dropped — "Next:  ()" on a
 *  touch with no next step is noise in something he pastes every evening. A line with no fields at
 *  all is kept as written, and an unknown `{token}` is left visible so a typo in the template
 *  shows rather than silently blanking a line. */
const blockLine = (line: string, row: Record<ExportField, string>): string | null => {
  let known = 0;
  let filled = 0;
  const rendered = line.replace(TOKEN, (whole, field: string) => {
    if (!Object.hasOwn(row, field)) return whole;
    known++;
    const value = row[field as ExportField];
    if (value !== '') filled++;
    return value;
  });
  return known > 0 && filled === 0 ? null : rendered;
};

/** The end-of-day block to paste: the rep's own template, one rendering per touch. */
export const touchesBlock = (touches: readonly Touch[], profile: ExportProfile): string =>
  touches
    .map((t) => {
      const row = exportRow(t, profile);
      return profile.block
        .split('\n')
        .map((line) => blockLine(line, row))
        .filter((line): line is string => line !== null)
        .join('\n');
    })
    .join('\n\n');

/** What the day did, for the line above the export buttons. */
export const touchSummary = (touches: readonly Touch[]): string => {
  const counts = new Map<TouchChannel, number>();
  for (const { visit } of touches) {
    const channel = visit.channel ?? 'walk_in';
    counts.set(channel, (counts.get(channel) ?? 0) + 1);
  }
  const parts = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([channel, n]) => `${n} ${n === 1 ? CHANNEL_LABELS[channel].toLowerCase() : CHANNEL_PLURALS[channel]}`);
  return parts.length ? parts.join(' · ') : 'Nothing logged yet.';
};
